<template>
  <v-card
    class="mx-auto"
    max-width="800"
    :loading="loading"
    :disabled="loading"
  >
    <v-card-title>{{ $t("queue_list.title") }}</v-card-title>
    <v-card-subtitle>
      {{ $t("queue_list.subtitle") }}
    </v-card-subtitle>

    <v-card-text class="px-3 px-md-6">
      <v-row dense>
        <v-col cols="12" sm="6">
          <v-autocomplete
            v-model="selectedYear"
            :items="yearOptions"
            outlined
            dense
            hide-details
            :label="$t('queue_list.year')"
            @change="onYearChange"
          />
        </v-col>
        <v-col cols="12" sm="6">
          <v-autocomplete
            v-model="selectedSemester"
            :items="semesterOptions"
            outlined
            dense
            hide-details
            :disabled="!selectedYear"
            :label="$t('queue_list.semester')"
            @change="onSemesterChange"
          />
        </v-col>
        <v-col v-if="selectedYear && selectedSemester" cols="12" sm="8">
          <v-autocomplete
            v-model="selectedSubjectId"
            :items="subjectOptions"
            outlined
            dense
            clearable
            hide-details
            :disabled="!selectedYear || !selectedSemester"
            :label="$t('queue_list.subject_id')"
            :no-data-text="$t('queue_list.no_subjects')"
            @change="onSubjectChange"
          />
        </v-col>
        <v-col v-if="selectedYear && selectedSemester" cols="12" sm="4">
          <v-autocomplete
            v-model="selectedSection"
            :items="sectionOptions"
            outlined
            dense
            clearable
            hide-details
            :disabled="!selectedSubjectId"
            :label="$t('queue_list.section')"
            @change="onSectionChange"
          />
        </v-col>
      </v-row>

      <v-alert v-if="error" type="error" dense outlined class="mt-4 mb-0">
        {{ $t("queue_list.error") }}
      </v-alert>

      <v-card v-if="teachTableData" outlined class="mt-4">
        <v-card-text>
          <v-row dense>
            <v-col cols="12" sm="8">
              <div class="text-caption blue-grey--text">
                {{ $t("queue_list.subject_name") }}
              </div>
              <a class="subject-link" @click="openSubjectDialog">
                {{ teachTableData.subject_id }} - {{ subjectName }}
              </a>
            </v-col>
            <v-col cols="12" sm="4">
              <div class="text-caption blue-grey--text">
                {{ $t("queue_list.capacity") }}
              </div>
              <div>{{ capacity }}</div>
            </v-col>
            <v-col cols="12">
              <div class="text-caption blue-grey--text">
                {{ $t("queue_list.instructors") }}
              </div>
              <div v-if="teacherList" v-html="teacherList"></div>
              <div v-else>-</div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <v-row dense class="mt-4">
        <v-col cols="12" sm="6">
          <v-btn
            block
            color="success"
            :disabled="!canSearch"
            @click="fetchQueue"
          >
            <v-icon left>mdi-magnify</v-icon>
            {{ $t("queue_list.search") }}
          </v-btn>
        </v-col>
        <v-col cols="12" sm="6">
          <v-btn block outlined color="blue-grey" @click="clearCourseFilter">
            <v-icon left>mdi-close</v-icon>
            {{ $t("queue_list.clear") }}
          </v-btn>
        </v-col>
      </v-row>

      <v-alert
        v-if="!loading && searched && queue.length === 0 && !error"
        type="info"
        dense
        outlined
        class="mt-4 mb-0"
      >
        {{ $t("queue_list.no_queue") }}
      </v-alert>

      <div v-if="queue.length > 0" class="queue-table mt-4">
        <v-simple-table dense class="text-no-wrap">
          <template v-slot:default>
            <thead class="blue-grey darken-1">
              <tr>
                <th class="white--text text-center font-weight-regular">#</th>
                <th class="white--text font-weight-regular">
                  {{ $t("queue_list.student_id") }}
                </th>
                <th class="white--text text-center font-weight-regular">
                  {{ $t("queue_list.status") }}
                </th>
                <th class="white--text font-weight-regular">
                  {{ $t("queue_list.expire_date") }}
                </th>
                <th class="white--text font-weight-regular">
                  {{ $t("queue_list.created_date") }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(item, index) in queue"
                :key="`${item.student_id}-${index}`"
              >
                <td class="text-center">{{ item.number }}</td>
                <td>{{ item.student_id }}</td>
                <td class="text-center">
                  <v-chip
                    small
                    :color="item.granted ? 'green lighten-4' : 'amber lighten-4'"
                    :text-color="item.granted ? 'green darken-3' : 'orange darken-2'"
                  >
                    {{ item.granted ? $t("queue_list.granted") : $t("queue_list.waiting") }}
                  </v-chip>
                </td>
                <td>{{ formatDateTime(item.EXPIRE_TIMESTAMP) }}</td>
                <td>{{ formatDateTime(item.created_at) }}</td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </div>
    </v-card-text>

    <subject-dialog ref="subjectDialog" :level-id="1" />
  </v-card>
</template>

<script>
import RegistrationProvider from "@/services/registration.provider.js";
import SubjectDialog from "@/views/teach_table/components/SubjectDialog.vue";
import { dateTimeFromDBTranslatorFull } from "@/helper/translator";

const RegistrationService = new RegistrationProvider();
const semesterOrder = ["3", "2", "1"];

export default {
  name: "RegistrationQueue",
  components: {
    SubjectDialog,
  },
  data: () => ({
    loading: false,
    error: false,
    searched: false,
    academicTerms: [],
    courseOptions: [],
    selectedYear: null,
    selectedSemester: null,
    selectedSubjectId: null,
    selectedSection: null,
    teachTableData: null,
    queue: [],
  }),
  computed: {
    yearOptions() {
      return [...new Set(this.academicTerms.map((item) => String(item.year)))]
        .sort((a, b) => Number(b) - Number(a))
        .map((year) => ({
          text: this.formatAcademicYear(year),
          value: year,
        }));
    },
    semesterOptions() {
      const available = new Set(
        this.academicTerms
          .filter((item) => String(item.year) === String(this.selectedYear))
          .map((item) => String(item.semester))
      );
      return semesterOrder.filter((semester) => available.has(semester));
    },
    subjectOptions() {
      return [...new Set(this.courseOptions.map((item) => item.subject_id))];
    },
    sectionOptions() {
      return this.courseOptions
        .filter((item) => item.subject_id === this.selectedSubjectId)
        .map((item) => item.section);
    },
    selectedCourse() {
      return this.courseOptions.find(
        (item) =>
          item.subject_id === this.selectedSubjectId &&
          Number(item.section) === Number(this.selectedSection)
      );
    },
    canSearch() {
      return Boolean(
        this.selectedYear &&
          this.selectedSemester &&
          this.selectedSubjectId &&
          Number.isInteger(this.selectedSection)
      );
    },
    subjectName() {
      if (!this.teachTableData) return "-";
      return this.teachTableData.subject_ename || "-";
    },
    teacherList() {
      if (!this.teachTableData) return "";
      return this.$i18n.locale === "en"
        ? this.teachTableData.teacher_list_en
        : this.teachTableData.teacher_list_th;
    },
    capacity() {
      if (!this.teachTableData) return "-";
      const limit = this.teachTableData.limit;
      if (limit === null || limit === "") return "-";
      return Number(limit) === 0
        ? this.$t("queue_list.unlimited")
        : limit;
    },
  },
  methods: {
    formatAcademicYear(year) {
      const numericYear = Number(year);
      if (this.$i18n.locale === "en" && numericYear > 2400) {
        return numericYear - 543;
      }
      return year;
    },
    formatDateTime(value) {
      return value ? dateTimeFromDBTranslatorFull(value) : "-";
    },
    buildNumberedQueue(rows) {
      let number = 0;
      return rows.map((row) => {
        const granted = String(row.GRANTED) === "1";
        if (!granted) number += 1;
        return { ...row, granted, number: granted ? "-" : number };
      });
    },
    resetCourseSelection() {
      this.selectedSubjectId = null;
      this.selectedSection = null;
      this.teachTableData = null;
      this.queue = [];
      this.searched = false;
      this.error = false;
    },
    onYearChange() {
      this.resetCourseSelection();
      this.selectedSemester = null;
      this.courseOptions = [];
      this.syncQuery();
    },
    async onSemesterChange() {
      this.resetCourseSelection();
      await this.refreshCourseOptions();
    },
    onSubjectChange() {
      this.selectedSection = null;
      this.teachTableData = null;
      this.queue = [];
      this.searched = false;
      this.error = false;
      this.syncQuery();
    },
    async onSectionChange() {
      this.queue = [];
      this.searched = false;
      this.error = false;
      try {
        this.loading = true;
        await this.fetchTeachTableData();
        this.syncQuery();
      } catch (error) {
        this.error = true;
        this.showApiError(error);
      } finally {
        this.loading = false;
      }
    },
    clearCourseFilter() {
      this.resetCourseSelection();
      this.syncQuery();
    },
    syncQuery() {
      this.$router
        .replace({
          name: "registration_queue",
          query: {
            year: this.selectedYear || undefined,
            semester: this.selectedSemester || undefined,
            subject_id: this.selectedSubjectId || undefined,
            section:
              this.selectedSection !== null ? this.selectedSection : undefined,
          },
        })
        .catch(() => {});
    },
    async loadCourseOptions() {
      this.courseOptions = [];
      if (!this.selectedYear || !this.selectedSemester) return;

      const data = await RegistrationService.getOfficerQueueSubjects({
        year: this.selectedYear,
        semester: this.selectedSemester,
      });
      this.courseOptions = data.subjects || [];
    },
    async refreshCourseOptions() {
      try {
        this.loading = true;
        this.error = false;
        await this.loadCourseOptions();
        this.syncQuery();
      } catch (error) {
        this.error = true;
        this.showApiError(error);
      } finally {
        this.loading = false;
      }
    },
    async fetchTeachTableData() {
      this.teachTableData = null;
      if (!this.selectedCourse) return;
      this.teachTableData = await RegistrationService.getTeachTableData(
        this.selectedCourse.TEACH_TABLE_ID
      );
    },
    openSubjectDialog() {
      if (this.$refs.subjectDialog && this.teachTableData) {
        this.$refs.subjectDialog.show_item_detail(this.teachTableData.subject_id, 1);
      }
    },
    async fetchQueue() {
      if (!this.canSearch) return;

      try {
        this.loading = true;
        this.error = false;
        this.searched = true;
        if (!this.teachTableData) await this.fetchTeachTableData();
        const data = await RegistrationService.getOfficerQueueList({
          year: this.selectedYear,
          semester: this.selectedSemester,
          subject_id: this.selectedSubjectId,
          section: this.selectedSection,
        });
        this.queue = this.buildNumberedQueue(data || []);
        this.syncQuery();
      } catch (error) {
        this.error = true;
        this.queue = [];
        this.showApiError(error);
      } finally {
        this.loading = false;
      }
    },
    showApiError(error) {
      const message =
        error && error.response && error.response.data
          ? error.response.data.message
          : null;
      if (message && this.$toasted) {
        this.$toasted.error(String(message), { duration: 3000 });
      }
    },
    termExists(year, semester) {
      return this.academicTerms.some(
        (item) =>
          String(item.year) === String(year) &&
          String(item.semester) === String(semester)
      );
    },
    async fetchInit() {
      try {
        this.loading = true;
        this.error = false;
        const options = await RegistrationService.getOfficerQueueAcademicTerms();
        this.academicTerms = options.academic_terms || [];

        const queryYear = this.$route.query.year;
        const querySemester = this.$route.query.semester;
        if (this.termExists(queryYear, querySemester)) {
          this.selectedYear = String(queryYear);
          this.selectedSemester = String(querySemester);
          await this.loadCourseOptions();
        } else if (
          this.academicTerms.some(
            (item) => String(item.year) === String(queryYear)
          )
        ) {
          this.selectedYear = String(queryYear);
        }

        const querySubjectId = this.$route.query.subject_id;
        const querySection = this.$route.query.section;
        const queryCourse = this.courseOptions.find(
          (item) =>
            item.subject_id === querySubjectId &&
            Number(item.section) === Number(querySection)
        );
        if (queryCourse) {
          this.selectedSubjectId = queryCourse.subject_id;
          this.selectedSection = queryCourse.section;
          await this.fetchTeachTableData();
          await this.fetchQueue();
        } else {
          this.syncQuery();
        }
      } catch (error) {
        this.error = true;
        this.showApiError(error);
      } finally {
        this.loading = false;
      }
    },
  },
  created() {
    this.fetchInit();
  },
};
</script>

<style scoped>
.subject-link {
  cursor: pointer;
  text-decoration: none;
}

.queue-table {
  overflow-x: auto;
}
</style>
